export type NomineeAddressCarrier = {
  sameAddressAsPrimaryHolder?: boolean;
  address1?: string;
  address2?: string;
  address3?: string;
  city?: string;
  state?: string;
  pincode?: string;
  country?: string;
};

export type ResolvedNomineeAddress = {
  address1: string;
  address2: string;
  address3: string;
  pincode: string;
  city: string;
  state: string;
  country: string;
};

const trim = (value?: string | number | null) =>
  value == null ? "" : String(value).trim();

export const normalizeCountryForNomineeApi = (country?: string | null): string => {
  const normalized = trim(country).toUpperCase();
  if (!normalized || normalized === "IN" || normalized === "INDIA") return "India";
  return trim(country);
};

/** Resolve primary-holder address from applicant Redux/API data and investor-service folio holder. */
export const resolvePrimaryHolderAddressForNominee = (sources: {
  applicant?: Record<string, unknown>;
  investorServiceHolder?: Record<string, unknown>;
}): ResolvedNomineeAddress => {
  const applicant = sources.applicant ?? {};
  const holder = sources.investorServiceHolder ?? {};

  let address1 = trim(applicant.addressLine1 as string);
  let address2 = trim(applicant.addressLine2 as string);
  let address3 = "";
  let pincode = trim(applicant.pincode as string);
  let city = trim(applicant.city as string);
  let state = trim(applicant.state as string);
  let country = normalizeCountryForNomineeApi(applicant.country as string);

  if (!address1) address1 = trim(holder.add1 as string);
  if (!address2) address2 = trim(holder.add2 as string);
  if (!address3) address3 = trim(holder.add3 as string);
  if (!pincode) pincode = trim(holder.pin as string);
  if (!city) city = trim(holder.city as string);
  if (!state) state = trim(holder.state as string);

  const holderCountry = trim(holder.country as string);
  if (holderCountry && (!country || country === "India")) {
    country = normalizeCountryForNomineeApi(holderCountry);
  }

  if (!address1) {
    const combined = trim(applicant.address as string);
    if (combined) address1 = combined;
  }

  if (!address1) address1 = trim(applicant.add1 as string);
  if (!address2) address2 = trim(applicant.add2 as string);

  if (!address1 && (city || state)) {
    address1 = [city, state].filter(Boolean).join(", ");
  }

  return { address1, address2, address3, pincode, city, state, country };
};

export const applyPrimaryHolderAddressToNominee = <T extends NomineeAddressCarrier>(
  nominee: T,
  sources: {
    applicant?: Record<string, unknown>;
    investorServiceHolder?: Record<string, unknown>;
  },
): T => {
  if (!nominee.sameAddressAsPrimaryHolder) return nominee;

  const resolved = resolvePrimaryHolderAddressForNominee(sources);
  return {
    ...nominee,
    address1: resolved.address1 || nominee.address1 || "",
    address2: resolved.address2 || nominee.address2 || "",
    address3: resolved.address3 || nominee.address3 || "",
    city: resolved.city || nominee.city || "",
    state: resolved.state || nominee.state || "",
    pincode: resolved.pincode || nominee.pincode || "",
    country: resolved.country || nominee.country || "India",
  };
};
